import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faEye,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { app } from "../firebase";
import swal from "sweetalert";
import {
  getFirestore,
  collection,
  orderBy,
  query,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

function RecordPaymentComp() {
  const db = getFirestore(app);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch invoices and orders
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const qInvoices = query(
          collection(db, "invoices"),
          orderBy("createdAt", "desc")
        );
        const snapshotInvoices = await getDocs(qInvoices);
        const invoicesData = snapshotInvoices.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const qOrders = query(collection(db, "Orders"));
        const snapshotOrders = await getDocs(qOrders);
        const ordersData = snapshotOrders.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setInvoices(invoicesData);
        setOrders(ordersData);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db]);

  // Handle marking invoice orders as paid
  const handleMarkAsPaid = async (invoice) => {
    swal({
      title: "Mark as Paid",
      text: `Are you sure you want to mark invoice for ${invoice.clientName} as Paid?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willMark) => {
      if (willMark) {
        try {
          if (invoice.orderIds && Array.isArray(invoice.orderIds)) {
            const updatePromises = invoice.orderIds.map((orderId) => {
              const orderRef = doc(db, "Orders", orderId);
              return updateDoc(orderRef, {
                paymentStatus: "paid",
                paymentDate: serverTimestamp(),
              });
            });
            await Promise.all(updatePromises);
          }

          // Refresh orders
          const qOrders = query(collection(db, "Orders"));
          const snapshotOrders = await getDocs(qOrders);
          const ordersData = snapshotOrders.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setOrders(ordersData);

          swal("Success", "Invoice orders marked as Paid.", "success");
        } catch (err) {
          console.error("Error updating orders:", err);
          swal("Error", "Failed to mark as Paid.", "error");
        }
      }
    });
  };

  // Compute invoice details dynamically
  const getInvoiceDetails = (invoice) => {
    const invoiceOrders = orders.filter((o) =>
      invoice.orderIds?.includes(o.id)
    );

    const totalAmount = invoiceOrders.reduce(
      (sum, o) => sum + (o.amount || 0),
      0
    );

    const allPaid =
      invoiceOrders.length > 0 &&
      invoiceOrders.every((o) => o.paymentStatus === "paid");

    const paymentDate = allPaid
      ? invoiceOrders[0]?.paymentDate?.toDate?.().toLocaleString() || "N/A"
      : "N/A";

    return {
      totalAmount,
      status: allPaid ? "paid" : "unpaid",
      paymentDate,
    };
  };

  const filteredInvoices = invoices.filter((inv) =>
    inv.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="dataComp p-4">
      <div className="dataCompTop d-flex justify-content-between align-items-center mb-3">
        <h5 className="fw-bold">Invoice Payments</h5>
        <div className="searchContainer d-flex align-items-center">
          <FontAwesomeIcon icon={faSearch} className="searchIcon text-muted" />
          <input
            type="text"
            placeholder="Search Invoice"
            className="form-control"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
          <p className="mt-2">Loading invoices...</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Client</th>
              <th>Number of Orders</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Payment Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => {
              const details = getInvoiceDetails(invoice);

              return (
                <tr key={invoice.id}>
                  <td>{invoice.clientName}</td>
                  <td>{invoice.orderIds?.length || 0}</td>
                  <td>KES {details.totalAmount.toFixed(2)}</td>
                  <td>
                    {details.status === "paid" ? (
                      <span className="text-success fw-bold">Paid</span>
                    ) : (
                      <span className="text-danger fw-bold">Unpaid</span>
                    )}
                  </td>
                  <td>{details.paymentDate}</td>
                  <td className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() =>
                        window.open(`/invoice/${invoice.clientId}`, "_blank")
                      }
                    >
                      <FontAwesomeIcon icon={faEye} /> View
                    </Button>
                    {details.status !== "paid" && (
                      <Button
                        size="sm"
                        variant="success"
                        onClick={() => handleMarkAsPaid(invoice)}
                      >
                        <FontAwesomeIcon icon={faCheck} /> Mark as Paid
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default RecordPaymentComp;